import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CopilotPanel from "@/components/CopilotPanel";
import logo from "@/assets/logo.png";
import logoAeliam from "@/assets/logo-aeliam-white.png";
import {
  ChartPieSlice, MagnifyingGlass, Kanban, ChatsCircle, FolderLock,
  FilePdf, SignOut, Sparkle, FolderSimple, Crosshair, PaperPlaneTilt, GearSix, ClipboardText,
  RocketLaunch, UsersThree, AddressBook, Key,
} from "@phosphor-icons/react";

const MA_NAV = [
  { to: "/", label: "Tableau de bord", icon: ChartPieSlice, end: true },
  { to: "/sourcing", label: "Deal Sourcing", icon: MagnifyingGlass },
  { to: "/prospection", label: "Prospection ciblée", icon: Crosshair },
  { to: "/dossiers", label: "Dossiers", icon: FolderSimple },
  { to: "/pipeline", label: "Pipeline M&A", icon: Kanban },
  { to: "/crm", label: "CRM Négociation", icon: ChatsCircle },
  { to: "/campagnes", label: "Suivi campagnes", icon: PaperPlaneTilt },
  { to: "/data-room", label: "Data Room", icon: FolderLock },
  { to: "/reporting", label: "Reporting", icon: FilePdf },
];

const AELIAM_NAV = [
  { to: "/aeliam", label: "Prospection Aeliam", icon: RocketLaunch, end: true },
  { to: "/contacts", label: "Contacts LinkedIn", icon: AddressBook },
  { to: "/aeliam/pipeline", label: "Pipeline Aeliam", icon: UsersThree },
];

const linkClass = (aeliam) => ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors border-l-2 ${
    isActive
      ? (aeliam ? "border-indigo-400 bg-indigo-500/10 text-indigo-300" : "border-primary bg-accent text-primary")
      : (aeliam ? "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary")
  }`;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const role = user?.role;
  const isSalesAeliam = role === "sales_aeliam";
  const mainNav = isSalesAeliam ? AELIAM_NAV : MA_NAV;
  const showAeliamSection = role === "super_admin";
  const { pathname } = useLocation();
  const aeliamContext = isSalesAeliam || pathname.startsWith("/aeliam") || pathname === "/contacts" || pathname === "/cles-api";

  return (
    <div className={`min-h-screen flex bg-background ${aeliamContext ? "theme-aeliam" : ""}`}>
      {/* Sidebar */}
      <aside className={`w-60 shrink-0 border-r border-border ${aeliamContext ? "bg-zinc-950" : "bg-white"} flex flex-col fixed h-screen`}>
        <div className="px-5 h-16 flex items-center border-b border-border">
          {aeliamContext
            ? <img src={logoAeliam} alt="Aeliam" className="h-7 w-auto" data-testid="brand-logo" />
            : <img src={logo} alt="AI Assurances" className="h-7 w-auto" data-testid="brand-logo" />}
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {mainNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1).replace(/\//g, "-")}`}
              className={linkClass(aeliamContext)}>
              <n.icon size={18} weight="regular" />
              {n.label}
            </NavLink>
          ))}
          {isSalesAeliam && (
            <NavLink to="/cles-api" data-testid="nav-cles-api" className={linkClass(aeliamContext)}>
              <Key size={18} weight="regular" />
              Ma clé API
            </NavLink>
          )}
          {showAeliamSection && (
            <>
              <div className="overline text-muted-foreground px-3 pt-4 pb-1">Aeliam · Sales</div>
              {AELIAM_NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  data-testid={`nav-${n.to.slice(1).replace(/\//g, "-")}`}
                  className={linkClass(aeliamContext)}>
                  <n.icon size={18} weight="regular" />
                  {n.label}
                </NavLink>
              ))}
              <NavLink to="/cles-api" data-testid="nav-cles-api" className={linkClass(aeliamContext)}>
                <Key size={18} weight="regular" />
                Clés API & Webhook
              </NavLink>
            </>
          )}
          {role === "super_admin" && (
            <NavLink to="/journal-audit" data-testid="nav-journal-audit" className={linkClass(aeliamContext)}>
              <ClipboardText size={18} weight="regular" />
              Journal d'audit
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-border">
          {!isSalesAeliam && !aeliamContext && (
            <button data-testid="open-copilot-btn" onClick={() => setCopilotOpen(true)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold bg-[#EEF2FF] text-[#4f46e5] border border-[#c7d2fe] hover:bg-[#e0e7ff] transition-colors mb-2">
              <Sparkle size={18} weight="fill" /> IA Copilot
            </button>
          )}
          <div className="flex items-center justify-between px-2 py-1">
            <button onClick={() => navigate("/parametres")} data-testid="nav-settings"
              className="flex items-center gap-2 min-w-0 text-left hover:text-primary group">
              <GearSix size={16} className="text-muted-foreground group-hover:text-primary shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-semibold truncate">{user?.name}</div>
                <div className="overline text-muted-foreground">{user?.role}</div>
              </div>
            </button>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
              className="text-muted-foreground hover:text-destructive p-1">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-60 min-w-0">
        <Outlet context={{ openCopilot: () => setCopilotOpen(true) }} />
      </main>

      <CopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  );
}
